import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/common/SectionHeading";
import SponsorCard from "@/components/common/SponsorCard";
import AdBanner from "@/components/common/AdBanner";
import { Spinner, EmptyState } from "@/components/common/Primitives";
import { useSponsors } from "@/hooks/useSiteQueries";

export default function SponsorsPage() {
  const { data: allSponsors, isPending } = useSponsors();
  const [category, setCategory] = useState("All");

  const categories = useMemo(() => {
    if (!allSponsors) return ["All"];
    return ["All", ...Array.from(new Set(allSponsors.map((s) => s.category).filter(Boolean)))];
  }, [allSponsors]);

  const sponsors = useMemo(() => {
    if (!allSponsors) return null;
    return allSponsors.filter(
      (s) => !category || category === "All" || s.category === category
    );
  }, [allSponsors, category]);

  return (
    <div data-testid="sponsors-page">
      <section className="bg-maroon text-white">
        <div className="container-bcs py-20 sm:py-24">
          <p className="eyebrow !text-gold-light mb-4">Sponsors & Well-Wishers</p>
          <h1 className="heading-xl !text-white max-w-3xl">
            The supporters who <span className="italic text-gold-light">stand with us</span>
          </h1>
          <p className="mt-6 text-white/80 max-w-2xl text-lg leading-relaxed">
            Businesses, parishes, families and well-wishers who make our community mission possible.
          </p>
        </div>
      </section>

      <section className="container-bcs py-14 sm:py-16">
        <AdBanner placement="sponsors_page" variant="hero" />

        <div className="mt-12 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                category === c
                  ? "bg-maroon text-white"
                  : "bg-white border border-gray-200 text-ink-soft hover:border-maroon hover:text-maroon"
              }`}
              data-testid={`sponsor-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        {isPending ? (
          <Spinner className="py-16" />
        ) : sponsors.length === 0 ? (
          <EmptyState title="No sponsors in this category" />
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sponsors.map((s) => (
              <SponsorCard key={s.id} sponsor={s} />
            ))}
          </div>
        )}

        <div className="mt-16 card-bcs p-8 sm:p-10 text-center bg-cream-alt">
          <SectionHeading
            align="center"
            title="Become a sponsor"
            subtitle="Support Sabha Day and our community programmes. Contact the office to learn about sponsorship opportunities."
          />
          <Link to="/contact" className="btn-primary mt-6 inline-flex" data-testid="sponsor-contact-btn">
            Get in touch
          </Link>
        </div>
      </section>
    </div>
  );
}
